import React from 'react';
import { useTutorial } from '../../../context/TutorialContext';
import { getAllTutorials } from '../../../config/tutorials.config';
import TutorialPrompt from './TutorialPrompt';
import TutorialBar from './TutorialBar';
import TutorialVideoPlayer from './TutorialVideoPlayer';

/**
 * Tutorial Manager Component
 * Manages displaying tutorial prompts, bars, and video player
 */
const TutorialManager = () => {
  const {
    activeTutorial,
    pendingTutorials,
    handleTutorialAction,
    showPendingTutorial,
    closeTutorial,
    removePendingTutorial
  } = useTutorial();

  const [showVideoPlayer, setShowVideoPlayer] = React.useState(false);
  const [currentVideoTutorial, setCurrentVideoTutorial] = React.useState(null);

  // Get all available tutorials for the "other tutorials" list
  const allTutorials = getAllTutorials();

  const handlePlay = async (tutorial) => {
    if (!tutorial) {
      return;
    }
    await handleTutorialAction(tutorial.featureId, 'watched');
    setCurrentVideoTutorial(tutorial);
    setShowVideoPlayer(true);
  };

  const handleVideoClose = () => {
    setShowVideoPlayer(false);
    setCurrentVideoTutorial(null);
  };

  const handleVideoWatched = () => {
    if (currentVideoTutorial) {
      handleTutorialAction(currentVideoTutorial.featureId, 'watched');
    }
    // Don't close automatically - let user see next video button
  };

  const handleNextVideo = async (nextTutorial) => {
    if (nextTutorial) {
      // Mark current video as watched if not already
      if (currentVideoTutorial) {
        await handleTutorialAction(currentVideoTutorial.featureId, 'watched');
      }
      // Smoothly transition to next video without closing dialog
      // Just play the next tutorial - the video player will handle the transition
      await handlePlay(nextTutorial);
    }
  };

  const handlePromptClose = () => {
    closeTutorial();
  };

  const handlePromptPlay = () => {
    if (activeTutorial) {
      handlePlay(activeTutorial);
    }
  };

  const handlePromptRemindLater = () => {
    if (activeTutorial) {
      handleTutorialAction(activeTutorial.featureId, 'remind_later');
    }
  };

  const handlePromptDontShow = () => {
    if (activeTutorial) {
      handleTutorialAction(activeTutorial.featureId, 'dont_show');
    }
  };

  // Handle pending tutorials - show the first one in the bar
  const firstPendingTutorial = pendingTutorials.length > 0 ? pendingTutorials[0] : null;
  const otherPendingTutorials = pendingTutorials.slice(1);

  const handleBarShowNow = () => {
    if (firstPendingTutorial) {
      showPendingTutorial(firstPendingTutorial.featureId);
    }
  };

  const handleBarLater = () => {
    if (firstPendingTutorial) {
      handleTutorialAction(firstPendingTutorial.featureId, 'remind_later');
    }
  };

  const handleBarDontShow = () => {
    if (firstPendingTutorial) {
      handleTutorialAction(firstPendingTutorial.featureId, 'dont_show');
    }
  };

  const handleBarClose = () => {
    // Just remove from pending without deferring - allows it to show again if triggered
    if (firstPendingTutorial) {
      removePendingTutorial(firstPendingTutorial.featureId);
    }
  };

  // Prepare other tutorials list for display
  const getOtherTutorialsList = (currentTutorial) => {
    const otherTutorials = allTutorials.filter(
      t => t.featureId !== currentTutorial?.featureId && t.videoSrc
    );
    
    return otherTutorials.map(tutorial => ({
      ...tutorial,
      onClick: () => {
        handlePlay(tutorial);
        closeTutorial();
      }
    }));
  };

  return (
    <>
      {/* Full prompt for first tutorial in session */}
      {activeTutorial && (
        <TutorialPrompt
          open={!!activeTutorial}
          onClose={handlePromptClose}
          featureName={activeTutorial.title || activeTutorial.featureId}
          description={activeTutorial.description}
          videoSrc={activeTutorial.videoSrc}
          onPlay={handlePromptPlay}
          onRemindLater={handlePromptRemindLater}
          onDontShow={handlePromptDontShow}
          otherTutorials={getOtherTutorialsList(activeTutorial)}
        />
      )}

      {/* Tiny bar for subsequent tutorials */}
      {!activeTutorial && firstPendingTutorial && (
        <TutorialBar
          featureName={firstPendingTutorial.title || firstPendingTutorial.featureId}
          onShowNow={handleBarShowNow}
          onLater={handleBarLater}
          onDontShow={handleBarDontShow}
          onClose={handleBarClose}
          otherTutorials={[
            ...otherPendingTutorials.map(t => ({
              ...t,
              onClick: () => {
                showPendingTutorial(t.featureId);
              }
            })),
            ...getOtherTutorialsList(firstPendingTutorial)
          ]}
        />
      )}

      {/* Video player */}
      {showVideoPlayer && currentVideoTutorial && (
        <TutorialVideoPlayer
          open={showVideoPlayer}
          onClose={handleVideoClose}
          videoSrc={currentVideoTutorial.videoSrc}
          title={currentVideoTutorial.title || currentVideoTutorial.featureId}
          transcript={currentVideoTutorial.transcript}
          onWatched={handleVideoWatched}
          featureId={currentVideoTutorial.featureId}
          onNextVideo={handleNextVideo}
        />
      )}
    </>
  );
};

export default TutorialManager;

